import { z } from 'zod';
import { AssocPriceType } from '../../constants/assoc-price-type.js';
import { optionalUuidString, requiredUuidString } from '../zod-helpers/index.js';
import { ASSOC_PRICE_CONSTRAINTS } from './constraints.js';
// productId is shared by setPriceAddOn (as part of a bigger payload) and removePriceAddOn (as
// part of its whole key), matching productToTreeInputSchema's link/unlink reuse.
const productIdSchema = requiredUuidString(`${ASSOC_PRICE_CONSTRAINTS.productId.label} is required.`, `Invalid ${ASSOC_PRICE_CONSTRAINTS.productId.label.toLowerCase()} selection.`);
// name is trimmed to match idx_assoc_price_product_name, the DB's lower(btrim(name)) unique
// index -- (productId, lower(btrim(name))) is the natural key now, not (productId,
// assocPriceTypeId): a product can carry more than one Percentage add-on (or Flat add-on) as
// long as each is named differently. Case-insensitive collisions are caught in the resolver via
// uniqueConstraintViolation and surfaced as a field error on name, not a generic toast.
const nameSchema = z
    .string()
    .trim()
    .min(1, `${ASSOC_PRICE_CONSTRAINTS.name.label} is required.`)
    .max(ASSOC_PRICE_CONSTRAINTS.name.maxLength, `${ASSOC_PRICE_CONSTRAINTS.name.label} must be ${ASSOC_PRICE_CONSTRAINTS.name.maxLength} characters or fewer.`);
// (productId, name) is the natural key for a single AssocPrice row -- shared by setPriceAddOn (as
// part of a bigger payload) and removePriceAddOn (as the whole payload).
const priceAddOnKeySchema = z.object({
    productId: productIdSchema,
    name: nameSchema,
});
// price is required and must be a positive decimal: an AssocPrice row IS its price adjustment,
// so there's no meaningful "set it to null" upsert UX -- clearing the adjustment removes the row
// entirely via removePriceAddOn. The Prisma column stays nullable only because it's shared with
// AssocPriceAud, whose audit rows record every field as nullable regardless of the entity's own
// invariants. Upper bound mirrors salesOrderLineUnitPriceSchema, staying well under the
// Decimal(28,9) column's overflow point.
export const setPriceAddOnInputSchema = priceAddOnKeySchema.extend({
    // The existing row's id, when editing an add-on in place -- lets the resolver upsert by
    // identity instead of by (productId, name), so renaming can't silently create a duplicate row
    // or overwrite an unrelated sibling that already owns the new name. Absent/null for the create
    // flow, which still upserts by the (productId, name) natural key above.
    id: optionalUuidString('Invalid add-on selection.').nullish(),
    // assoc_price_type is seeded as Percentage(1)/Flat(2), not the List COD/NET/MSRP tiers the
    // plan originally assumed -- see the NUDOOL-27 implementation plan's PR 5 correction note.
    // z.enum against a numeric TS enum is the established pattern for an int-enum FK field in this
    // codebase (see createLocationInputSchema's typeId), not a string-typed form helper.
    assocPriceTypeId: z.enum(AssocPriceType, 'Invalid price type selection.'),
    price: z
        .number()
        .positive(`${ASSOC_PRICE_CONSTRAINTS.price.label} must be greater than 0.`)
        .max(ASSOC_PRICE_CONSTRAINTS.price.max, `${ASSOC_PRICE_CONSTRAINTS.price.label} must be ${ASSOC_PRICE_CONSTRAINTS.price.max.toLocaleString()} or less.`),
    activeFlag: z.boolean().optional().nullable(),
});
export const removePriceAddOnInputSchema = priceAddOnKeySchema.extend({
    // Same reasoning as setPriceAddOnInputSchema's own `id` above: lets the resolver delete by
    // identity instead of by (productId, name), so a delete triggered from a stale list can't
    // resolve `name` to a different sibling row that was renamed to it in the meantime. Absent/null
    // falls back to the natural-key lookup (e.g. a caller with no row id in scope).
    id: optionalUuidString('Invalid add-on selection.').nullish(),
});
//# sourceMappingURL=input.js.map